import Phaser from 'phaser';
import type { SentienceGate } from '@/systems/SentienceGate';

/**
 * Screen-top narrator panel. The blob's inner monologue lives here.
 * Stays visible long enough to read comfortably on mobile. Stacks
 * the most recent line above a fading older line so the player can
 * catch a beat they almost missed.
 *
 * Deliberately NOT sentience-gated at the rendering level — if the
 * scene chooses to emit a narrator line, we show it. Sentience-gating
 * happens at the *source* (the scene decides when the blob is
 * articulate enough to think in words).
 */
const PANEL_PAD_X = 40;
const PANEL_PAD_Y = 14;
const LINE_FONT = '28px';
const LINE_FONT_MOBILE = '36px';

export class NarratorPanel {
  private bg: Phaser.GameObjects.Rectangle;
  private current: Phaser.GameObjects.Text;
  private previous: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    _gate: SentienceGate,
  ) {
    const { width } = scene.scale;
    const mobile = NarratorPanel.detectMobile();
    const fontSize = mobile ? LINE_FONT_MOBILE : LINE_FONT;

    this.bg = scene.add
      .rectangle(width / 2, 58, width - 100, 100, 0x000000, 0.55)
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0, 0)
      .setDepth(1000)
      .setVisible(false);

    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize,
      color: '#e8ffe8',
      fontFamily: 'monospace',
      align: 'center',
      stroke: '#000000',
      strokeThickness: 3,
      wordWrap: { width: width - PANEL_PAD_X * 2 },
    };

    this.current = scene.add
      .text(width / 2, 42, '', textStyle)
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0, 0)
      .setDepth(1001)
      .setAlpha(0);

    this.previous = scene.add
      .text(width / 2, 78, '', { ...textStyle, color: '#7a9a7a', fontSize: mobile ? '24px' : '20px' })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0, 0)
      .setDepth(1001)
      .setAlpha(0);
  }

  show(text: string, durationMs = 4500): void {
    // Push current → previous, bring new → current
    const oldCurrentText = this.current.text;
    if (oldCurrentText) {
      this.previous.setText(oldCurrentText).setAlpha(0.6);
      this.scene.tweens.add({
        targets: this.previous,
        alpha: 0,
        duration: 1600,
        delay: 1200,
      });
    }
    this.current.setText(text);
    this.bg.setVisible(true);
    this.scene.tweens.killTweensOf(this.current);
    this.scene.tweens.killTweensOf(this.bg);

    this.current.setAlpha(0);
    this.scene.tweens.add({
      targets: this.current,
      alpha: 1,
      duration: 250,
    });
    this.scene.tweens.add({
      targets: this.current,
      alpha: 0,
      delay: durationMs,
      duration: 800,
    });
    this.scene.tweens.add({
      targets: this.bg,
      alpha: 0,
      delay: durationMs,
      duration: 800,
      onComplete: () => {
        this.bg.setVisible(false);
        this.bg.setAlpha(0.55);
      },
    });
  }

  clear(): void {
    this.current.setText('');
    this.previous.setText('');
    this.bg.setVisible(false);
  }

  destroy(): void {
    this.bg.destroy();
    this.current.destroy();
    this.previous.destroy();
  }

  private static detectMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches ?? false;
  }
}
